import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { deepCopy, flatObjectToInterface, interfaceDataToFlatObject } from "../common";
export var PropertySchemaVersion;
(function (PropertySchemaVersion) {
    /**
     * Very first version of the property schema, whose value only supports string, boolean,
     * number and vectors
     */
    PropertySchemaVersion[PropertySchemaVersion["basicPropValue"] = 1] = "basicPropValue";
    /**
     * New schema where we introduce link value to property
     */
    PropertySchemaVersion[PropertySchemaVersion["linkPropValue"] = 2] = "linkPropValue";
})(PropertySchemaVersion || (PropertySchemaVersion = {}));
/**
 * The schema version to write all new data with
 *
 * This is set to v2, meaning any write going through this converter will update the property's schema to v2
 *
 * We use this to support updating v2 property (i.e. who supports LinkValue) in the backend, even tho we don't support that in frontend yet.
 */
export const currentPropertySchemaVersionToWrite = PropertySchemaVersion.linkPropValue;
export class BasePropertyConverter {
    metadataGenerator;
    constructor(metadataGenerator) {
        this.metadataGenerator = metadataGenerator;
    }
    /**
     * Gets the schema version from teh stored representation (via its metadata)
     */
    static getPropertySchemaVersion(stored) {
        return stored.metadata?.propertySchemaVersion || PropertySchemaVersion.basicPropValue;
    }
    /**
     * Load property data from an object by delegating to functions that can read data with a given schema.
     *
     * @param rawStoredData Data of unknown type or schema version.
     * @returns An IProperty object
     * @throws Error if the schema version is not recognized.
     */
    deserialize(rawStoredData) {
        const data = deepCopy(flatObjectToInterface(rawStoredData).data);
        // Any property in latest schema can just pass through
        if (BasePropertyConverter.getPropertySchemaVersion(rawStoredData) >=
            PropertySchemaVersion.linkPropValue) {
            return data;
        }
        // Any datasheet property not in latests schema needs to support backward compatibility
        if (data.name === defaultPropertyDefinitions.datasheet_url.label && typeof data.value === "string") {
            return {
                ...data,
                value: {
                    type: "link",
                    url: data.value,
                    text: data.value,
                },
            };
        }
        // Any other property can pass through
        return data;
    }
    /**
     * Convert an IPropertyData object into an object with a schema version attached.
     */
    serialize(data, propertySchemaVersionToWrite = currentPropertySchemaVersionToWrite) {
        const result = deepCopy(data);
        if (propertySchemaVersionToWrite >= PropertySchemaVersion.linkPropValue) {
            // Only "Datasheet URL" prop is of type "link" and here we support backward
            // compatibility by serializing the string value from frontend to `LinkValue` stored in backend.
            // In other words, for any datasheet url props that are not using the LinkValue schema,
            // any update will update both text and value in the LinkValue
            // QUESTION: Was trying to do a more general support by checking `valueType` from the passed in prop, but that doesn't always seem to be set??
            if (data.name === defaultPropertyDefinitions.datasheet_url.label &&
                result.value !== undefined &&
                typeof result.value === "string") {
                const hypertextValue = {
                    type: "link",
                    text: result.value,
                    url: result.value,
                };
                result.value = hypertextValue;
            }
        }
        return interfaceDataToFlatObject({
            data: result,
            metadata: { ...this.metadataGenerator.generate(), propertySchemaVersion: propertySchemaVersionToWrite },
        });
    }
}
export class PropertyAdminConverter extends BasePropertyConverter {
    toFirestore(modelObject) {
        return this.serialize(modelObject);
    }
    fromFirestore(snapshot) {
        return this.deserialize(snapshot.data());
    }
}
export class PropertyClientConverter extends BasePropertyConverter {
    toFirestore(modelObject) {
        return this.serialize(modelObject);
    }
    fromFirestore(snapshot) {
        return this.deserialize(snapshot.data());
    }
}
//# sourceMappingURL=PropertyConverter.js.map